import { INSPECTION_TYPE_OPTIONS } from "constants/constants";
import routeNames from "constants/route-names";
import { getInspectionTypeLabel } from "utils/helpers";

// Parses a Doctrine brief answer into the sections the Brief message renders.
//
// A brief is a single Doctrine answer made of two pieces:
//   1. a ```json fence written by the database pass;
//   2. Doctrine's web-research pass, appended after it under
//      "### Community Reputation & Public News", one **Property** group per property.
// The JSON never describes the neighborhood, so the two pieces are parsed separately and joined.
// This mirrors the service's brief-answer.mjs, which feeds the Brief email from the same answer.

const WEB_RESEARCH_HEADING = "### Community Reputation & Public News";

// A property whose search found nothing answers "NO_FINDINGS: <reason>", and Doctrine swaps that
// for the sentence below while keeping the reason in its own logs. The prompt lives in the
// database and can reach a Doctrine deploy that predates the contract, so the token is rewritten
// here as well — otherwise it reaches the customer verbatim.
const NO_FINDINGS_PATTERN = /^NO_FINDINGS\s*:/i;
const NO_FINDINGS_TEXT = "No recent reviews or public news were found for this property.";

// The model sometimes narrates a sentence before opening the fence, so the fence is searched for
// rather than assumed to start the answer.
const JSON_FENCE = /```json\s*([\s\S]*?)```/i;

// The model reads its facts out of the database and copies the stored inspection type into the
// brief ("fire-final"), which is not how the app names it anywhere else. Longest value first, so
// "semiannual" is never read as the "annual" inside it.
const INSPECTION_TYPE_PATTERN = new RegExp(
	`\\b(${INSPECTION_TYPE_OPTIONS.map(({ value }) => value)
		.sort((a, b) => b.length - a.length)
		.join("|")})\\b`,
	"gi"
);

// Iris was renamed to Ollie, so the old name is never what the reader should see — not in the
// score it still carries in the database, and not anywhere else in the answer.
const IRIS_TERM = /iris/gi;

// A brief names devices, handovers and inspections in its prose, and a model that took the chat's
// linking rules writes each of those names as `[Name](/installed-devices/{id})`. The brief draws
// its own links from the ids in the JSON and renders the prose as text, so the link is left as its
// name.
const MARKDOWN_LINK = /\[([^\]\n]+)\]\([^()\s]*\)/g;

// Long lists are what break the JSON (the model starts repeating itself and degenerates
// mid-string), and the design only has room for a handful of cards anyway.
const MAX_ITEMS = 5;

export interface BriefAttentionLink {
	label: string;
	href: string | null;
	underline: boolean;
}

export interface BriefAttentionItem {
	title: string;
	propertyName: string | null;
	link: BriefAttentionLink | null;
	photoUrl: string | null;
	isHandover: boolean;
	status: string | null;
	body: string | null;
	// Used by BriefMessage to read the device status from the database.
	installedProductId: string | null;
}

export interface BriefCard {
	title: string;
	propertyName: string | null;
	date: string | null;
	body: string | null;
	linkHref: string | null;
}

export interface BriefDeviceProperty {
	name: string;
	online: number;
	offline: number;
}

export interface BriefNeighborhoodBlock {
	label: string;
	bullets?: string[];
	text?: string;
}

export interface Brief {
	intro: string | null;
	needsAttention: BriefAttentionItem[];
	deviceSummary: { properties: BriefDeviceProperty[] } | null;
	projects: BriefCard[];
	orders: BriefCard[];
	neighborhood: BriefNeighborhoodBlock[];
	properties: string[];
	// True when the database part of the brief failed and only the web research part came back.
	databaseUnavailable?: boolean;
}

export interface ParsedBriefAnswer {
	brief: Brief | null;
	markdown: string;
}

const text = (value: unknown): string | null =>
	typeof value === "string" && value.trim() ? value.trim() : null;

// A markdown link the model wrote around a name it retrieved, down to the name itself.
const unlink = (line: string): string => line.replace(MARKDOWN_LINK, "$1");

// What the reader gets to see, for the strings written by the model rather than read from a
// column: the prompt asks it to name things the way the app does, and this is what holds when it
// does not.
const friendly = (line: string): string =>
	unlink(line)
		.replace(INSPECTION_TYPE_PATTERN, match => getInspectionTypeLabel(match.toLowerCase()))
		.replace(IRIS_TERM, "Ollie");

// text(), for the strings the reader reads as prose. Ids, hrefs and the OFFLINE badge stay
// verbatim: they are matched or drawn, not read.
const copy = (value: unknown): string | null => {
	const line = text(value);
	return line && friendly(line);
};

const count = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0);

const take = <T,>(list: unknown): T[] => (Array.isArray(list) ? (list.slice(0, MAX_ITEMS) as T[]) : []);

// Deep links follow the same routes the brief prompt is told to collect ids for. Orders have no
// customer-facing detail route, so they land on the closest page.
const deepLink = (type: string | null, id: string | null): string | null => {
	if (!id) return null;
	switch (type) {
		case "installedProduct":
			return `${routeNames.INSTALLED_DEVICES}/${id}`;
		case "handover":
			return routeNames.HANDOVER_DETAILS.replace(":handoverId", id);
		case "inspection":
			return routeNames.OLLIE_INSPECTION_DETAILS.replace(":jobId", id);
		case "order":
			return routeNames.ORDER;
		default:
			return null;
	}
};

// When the model degenerates it leaves a string unterminated and nothing after it parses. Cutting
// the object back to an earlier top-level entry keeps the sections it wrote before that point.
const repairJson = (source: string): any => {
	const cutPoints: number[] = [];
	let depth = 0;
	let inString = false;
	let escaped = false;

	for (let i = 0; i < source.length; i++) {
		const char = source[i];
		if (escaped) escaped = false;
		else if (char === "\\") escaped = true;
		else if (char === '"') inString = !inString;
		else if (inString) continue;
		else if (char === "{" || char === "[") depth += 1;
		else if (char === "}" || char === "]") depth -= 1;
		else if (char === "," && depth === 1) cutPoints.push(i);
	}

	for (const cut of cutPoints.reverse().slice(0, 10)) {
		try {
			return JSON.parse(`${source.slice(0, cut)}}`);
		} catch {
			continue;
		}
	}
	return null;
};

// The sections the brief prompt asks for. A chat answer can carry a ```json fence for any reason,
// so an object is only treated as a brief when it names at least one of them.
const BRIEF_SECTIONS = ["intro", "needsAttention", "deviceSummary", "projects", "orders", "properties"];

const isBriefShaped = (data: unknown): boolean =>
	!!data && typeof data === "object" && !Array.isArray(data) && BRIEF_SECTIONS.some(key => key in data);

// Where the digest is inside the answer: `json` is what to parse, `raw` is what to cut out of the
// markdown fallback. The prompt asks for a ```json fence and usually gets one, but the model does
// sometimes write the object with no fence at all — and then every character of it was rendered to
// the customer as the body of their brief. So the first balanced { … } is accepted too.
const findJsonBlock = (answer: string): { json: string; raw: string; fenced?: boolean } | null => {
	const fenced = answer.match(JSON_FENCE);
	if (fenced) return { json: fenced[1], raw: fenced[0], fenced: true };

	const start = answer.indexOf("{");
	if (start === -1) return null;

	let depth = 0;
	let inString = false;
	let escaped = false;

	for (let i = start; i < answer.length; i++) {
		const char = answer[i];
		if (escaped) escaped = false;
		else if (char === "\\") escaped = true;
		else if (char === '"') inString = !inString;
		else if (inString) continue;
		else if (char === "{") depth += 1;
		else if (char === "}" && --depth === 0) {
			const raw = answer.slice(start, i + 1);
			return { json: raw, raw };
		}
	}

	// Unbalanced: the model was cut off mid-object. Everything up to the web-research heading is
	// still the digest, and repairJson knows how to walk a truncated object back.
	const heading = answer.indexOf(WEB_RESEARCH_HEADING, start);
	const raw = answer.slice(start, heading === -1 ? answer.length : heading);
	return { json: raw, raw };
};

const extractJson = (block: { json: string } | null): any => {
	if (!block) return null;

	let data: any;
	try {
		data = JSON.parse(block.json);
	} catch {
		data = repairJson(block.json);
	}

	return isBriefShaped(data) ? data : null;
};

// A fallback still reaches a customer, so the token has to be rewritten in the markdown too. The
// same goes for the names: prose the model wrote is prose someone reads.
const sanitizeMarkdown = (markdown: string): string =>
	friendly(
		markdown.replace(
			/^([ \t]*(?:[-*][ \t]+)?)NO_FINDINGS[ \t]*:.*$/gim,
			(_line, prefix) => `${prefix}${NO_FINDINGS_TEXT}`
		)
	);

// The model is asked for either findings or the "nothing found" line, never both. When it sends
// both anyway, the findings are the answer and the line is noise.
const dropRedundantNoFindings = (block: BriefNeighborhoodBlock): BriefNeighborhoodBlock => {
	const findings = (block.bullets || []).filter(bullet => bullet !== NO_FINDINGS_TEXT);
	return findings.length ? { ...block, bullets: findings } : block;
};

// Doctrine researches one property at a time and leaves out the ones whose search came back with
// nothing it could attribute to a source, so its section does not always cover the selection. The
// brief's own property list does, so the missing ones are filled in with the neutral sentence: a
// property that reports nothing still has to say so.
const coverEveryProperty = (
	blocks: BriefNeighborhoodBlock[],
	properties: string[],
): BriefNeighborhoodBlock[] => {
	// No section at all means the research never ran; inventing one for every property would
	// report an absence of news we never went looking for.
	if (!blocks.length || !properties.length) return blocks;

	const byLabel = new Map(blocks.map(block => [block.label.trim().toLowerCase(), block]));
	const covered = properties.map(
		name =>
			byLabel.get(name.trim().toLowerCase()) || { label: name, text: NO_FINDINGS_TEXT },
	);

	// Anything Doctrine wrote under a name the brief never listed still belongs in the section.
	const used = new Set(covered);
	return [...covered, ...blocks.filter(block => !used.has(block))];
};

// The web pass writes "**Property Name**" followed by "- finding" bullets, and a single bullet
// with no heading when it found nothing. Both become Neighborhood & Reviews blocks.
const parseWebResearch = (answer: string): BriefNeighborhoodBlock[] => {
	const start = answer.indexOf(WEB_RESEARCH_HEADING);
	if (start === -1) return [];

	const body = answer.slice(start + WEB_RESEARCH_HEADING.length).trim();
	if (!body) return [];

	const blocks: BriefNeighborhoodBlock[] = [];
	let current: BriefNeighborhoodBlock | null = null;

	for (const line of body.split("\n").map(l => l.trim())) {
		if (!line) continue;

		const heading = line.match(/^\*\*(.+?)\*\*$/);
		if (heading) {
			current = { label: heading[1], bullets: [] };
			blocks.push(current);
			continue;
		}

		const bullet = line.replace(/^[-*]\s+/, "");
		if (bullet === line) continue; // prose outside a bullet: not a finding

		if (!current) {
			current = { label: "Online reviews", bullets: [] };
			blocks.push(current);
		}
		// The reason behind a NO_FINDINGS line is ours to read in the logs, never the customer's.
		current.bullets!.push(NO_FINDINGS_PATTERN.test(bullet) ? NO_FINDINGS_TEXT : bullet);
	}

	// A block whose only bullet says nothing was found reads better as a plain sentence.
	return blocks
		.map(dropRedundantNoFindings)
		.filter(block => block.bullets?.length)
		.map(block =>
			block.bullets!.length === 1 && /^no /i.test(block.bullets![0])
				? { label: block.label, text: block.bullets![0] }
				: block
		);
};

const BULLET_PREFIX = /^[-*]\s+/;

// Returns true when the answer has a web research section with at least one finding: a bullet that
// is not a "nothing found" line. Keep in sync with briefWebResearchHasFindings in
// maetro-service/app/services/doctrine-ai.mjs.
const webResearchHasFindings = (answer: string): boolean => {
	const start = answer.indexOf(WEB_RESEARCH_HEADING);
	if (start === -1) return false;

	return answer
		.slice(start)
		.split("\n")
		.some(line => {
			const trimmedLine = line.trim();
			if (!BULLET_PREFIX.test(trimmedLine)) return false;

			const bullet = trimmedLine.replace(BULLET_PREFIX, "");
			return !NO_FINDINGS_PATTERN.test(bullet) && bullet !== NO_FINDINGS_TEXT && !/^no recent reviews/i.test(bullet);
		});
};

// The design gives an attention block one link line, holding the device name. Handovers have no
// device, so that line becomes the action that opens them instead.
const buildAttentionLink = (item: any): BriefAttentionLink | null => {
	const deviceName = text(item.deviceName);
	if (deviceName) {
		return {
			// The name comes from a column, so it is not run through friendly() — but the model
			// still hands it over wrapped in a link often enough to unwrap it here.
			label: unlink(deviceName),
			href: deepLink("installedProduct", text(item.installedProductId)),
			underline: false
		};
	}

	const handoverHref = deepLink("handover", text(item.handoverId));
	if (handoverHref) {
		return { label: "View handover →", href: handoverHref, underline: true };
	}

	return null;
};

const buildNeedsAttention = (items: unknown): BriefAttentionItem[] =>
	take<any>(items)
		.filter(item => text(item?.title))
		.map(item => ({
			title: copy(item.title) as string,
			propertyName: text(item.propertyName),
			link: buildAttentionLink(item),
			photoUrl: text(item.photoUrl),
			isHandover: !!text(item.handoverId),
			status: text(item.status),
			body: copy(item.body),
			installedProductId: text(item.installedProductId)
		}));

const buildCards = (items: unknown): BriefCard[] =>
	take<any>(items)
		.filter(item => text(item?.title))
		.map(item => ({
			title: copy(item.title) as string,
			propertyName: text(item.propertyName),
			date: text(item.date),
			body: copy(item.body),
			linkHref: deepLink(text(item.linkType), text(item.linkId))
		}));

const buildDeviceSummary = (deviceSummary: any): Brief["deviceSummary"] => {
	const properties = deviceSummary?.properties;
	if (!Array.isArray(properties) || !properties.length) return null;

	return {
		properties: properties
			.filter((property: any) => text(property?.name))
			.map((property: any) => ({
				name: text(property.name) as string,
				online: count(property.online),
				offline: count(property.offline)
			}))
	};
};

const buildProperties = (properties: unknown): string[] =>
	(Array.isArray(properties) ? properties : [])
		.map((property: any) => text(property?.name))
		.filter((name): name is string => !!name);

/**
 * Splits a brief answer into its structured sections plus the markdown to fall back to.
 * `citationText` is the same answer after citation markers have been inserted; the neighborhood
 * findings are read from it so grounded web results keep their markers, while the JSON is always
 * read from the untouched answer.
 */
export const parseBriefAnswer = (answer: string, citationText?: string): ParsedBriefAnswer => {
	const block = findJsonBlock(answer || "");
	const data = extractJson(block);
	// An unfenced object is only cut out of the markdown once it has proved to be the digest:
	// otherwise a prose answer that happens to contain braces would lose them.
	const cutJson = block && (data || block.fenced);
	const markdown = sanitizeMarkdown(
		cutJson && block ? (answer || "").replace(block.raw, "") : answer || ""
	).trim();

	// The database part failed and only the web research part came back. The brief is the news
	// section alone, and `databaseUnavailable` shows a notice in place of the property sections.
	if (!data && !JSON_FENCE.test(answer || "") && webResearchHasFindings(answer || "")) {
		return {
			brief: {
				intro: null,
				needsAttention: [],
				deviceSummary: null,
				projects: [],
				orders: [],
				neighborhood: parseWebResearch(citationText || answer || ""),
				properties: [],
				databaseUnavailable: true
			},
			markdown
		};
	}

	if (!data) return { brief: null, markdown };

	const properties = buildProperties(data.properties);

	return {
		brief: {
			intro: copy(data.intro),
			needsAttention: buildNeedsAttention(data.needsAttention),
			deviceSummary: buildDeviceSummary(data.deviceSummary),
			projects: buildCards(data.projects),
			orders: buildCards(data.orders),
			neighborhood: coverEveryProperty(
				parseWebResearch(citationText || answer || ""),
				properties
			),
			properties
		},
		markdown
	};
};
